import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import queryString from 'query-string';

import { NoItemsPanel, PageContentPreloader, PageLayout, PanelTabs } from 'common/components';
import noPartnersImage from 'common/images/noPartners.svg';

import { getCurrentIBPrograms, getSimpleIBSystemSettings } from '../../_redux';
import { SimpleIBSystemSettingsPanel } from '../../_components';
import CurrentIBProgramsItem from '../../_components/CurrentIBProgramsItem/CurrentIBProgramsItem';
import CurrentSubIBProgramsItem from '../../_components/CurrentSubIBProgramsItem/CurrentSubIBProgramsItem';
import SubIBProgramsTree from '../../_components/SubIBProgramsTree/SubIBProgramsTree';

const PartnershipCurrentIBProgramsPage = ({
  partnershipSystem,
  getCurrentIBPrograms,
  currentIBPrograms,
  currentIBProgramsIsLoaded,
  getSimpleIBSystemSettings,
  simpleIBSystemSettings,
  simpleIBSystemSettingsAreLoaded,
  location,
}) => {
  const simplePartnerSystem = partnershipSystem === 'simple';
  const [activeTabSlug, setActiveTabSlug] = useState('');
  const [activeMode, setActiveMode] = useState('ib'); // 'ib' 或 'subib'
  const [selectedRewardData, setSelectedRewardData] = useState(null);
  const [selectedNodeName, setSelectedNodeName] = useState('');

  useEffect(() => {
    if (!simplePartnerSystem) {
      getCurrentIBPrograms();
    } else {
      getSimpleIBSystemSettings();
    }
  }, [getCurrentIBPrograms, simplePartnerSystem, getSimpleIBSystemSettings]);

  useEffect(() => {
    // 从URL查询参数中获取tab值，如果有的话
    const query = queryString.parse(location.search);
    if (query.tab) {
      setActiveTabSlug(query.tab);
    } else {
      // 设置第一个有效的IBProgram作为默认激活的tab
      const validPrograms = currentIBPrograms.filter(
        ibProgram => ibProgram.partnershipConditionsMultiple && ibProgram.partnershipConditionsMultiple.length > 0
      );
      if (validPrograms.length > 0 && !activeTabSlug) {
        setActiveTabSlug(validPrograms[0].partnerCode);
      }
    }
  }, [currentIBPrograms, activeTabSlug, location.search]);

  // 当切换到Sub IB模式时，自动选中根节点
  useEffect(() => {
    if (activeMode !== 'ib') {
      const validPrograms = currentIBPrograms.filter(
        ibProgram =>
          ibProgram.partnershipConditionsMultiple &&
          ibProgram.partnershipConditionsMultiple.length > 0 &&
          ibProgram.partnershipConditionsMultipleSub
      );
      if (validPrograms.length > 0) {
        const sub = validPrograms[0].partnershipConditionsMultipleSub;
        if (sub && sub.reward_data) {
          setSelectedRewardData(sub.reward_data);
          setSelectedNodeName(sub.tree_data ? sub.tree_data.name : '');
        }
      }
    }
  }, [activeMode, currentIBPrograms]);

  // 当切换标签页时，更新选中的节点数据（针对Sub IB模式）
  useEffect(() => {
    if (activeMode !== 'ib' && activeTabSlug) {
      const currentProgram = currentIBPrograms.find(ibProgram => ibProgram.partnerCode === activeTabSlug);
      if (currentProgram && currentProgram.partnershipConditionsMultipleSub) {
        const sub = currentProgram.partnershipConditionsMultipleSub;
        if (sub && sub.reward_data) {
          setSelectedRewardData(sub.reward_data);
          setSelectedNodeName(sub.tree_data ? sub.tree_data.name : '');
        }
      }
    }
  }, [activeMode, activeTabSlug, currentIBPrograms]);

  const handleSelectNode = node => {
    setSelectedRewardData(node.reward_data || null);
    setSelectedNodeName(node.name || '');
  };

  return (
    <PageLayout captionSlug="partnershipPrograms">
      {simplePartnerSystem ? (
        <PageContentPreloader ready={simpleIBSystemSettingsAreLoaded} type="line">
          <SimpleIBSystemSettingsPanel simpleIBSystemSettings={simpleIBSystemSettings} />
        </PageContentPreloader>
      ) : (
        <PageContentPreloader ready={currentIBProgramsIsLoaded} type="line">
          {currentIBPrograms.some(
            ibProgram => ibProgram.partnershipConditionsMultiple && ibProgram.partnershipConditionsMultiple.length > 0
          ) ? (
            <PanelTabs activeTabSlug={activeTabSlug}>
              {currentIBPrograms
                .filter(
                  ibProgram =>
                    ibProgram.partnershipConditionsMultiple && ibProgram.partnershipConditionsMultiple.length > 0
                )
                .map(ibProgram => (
                  <div
                    key={ibProgram.partnerCode}
                    slug={ibProgram.partnerCode}
                    textId={ibProgram.partnerCode}
                    noTranslate>
                    {/* 模式切换按钮 */}
                    <div style={{ marginBottom: '20px' }}>
                      <button
                        style={{
                          padding: '8px 16px',
                          marginRight: '10px',
                          border: '1px solid #ccc',
                          borderRadius: '4px',
                          backgroundColor: activeMode === 'ib' ? '#2C008F' : 'white',
                          color: activeMode === 'ib' ? 'white' : '#333',
                          cursor: 'pointer',
                        }}
                        onClick={() => setActiveMode('ib')}>
                        IB
                      </button>
                      <button
                        style={{
                          padding: '8px 16px',
                          border: '1px solid #ccc',
                          borderRadius: '4px',
                          backgroundColor: activeMode === 'subib' ? '#2C008F' : 'white',
                          color: activeMode === 'subib' ? 'white' : '#333',
                          cursor: 'pointer',
                        }}
                        onClick={() => setActiveMode('subib')}>
                        Sub IB
                      </button>
                    </div>

                    {activeMode === 'ib' &&
                      ibProgram.partnershipConditionsMultiple.map((condition, index) => (
                        <div
                          key={index}
                          style={{
                            marginBottom: '10px',
                          }}>
                          <CurrentIBProgramsItem partnerCode={ibProgram.partnerCode} conditions={condition} />
                        </div>
                      ))}
                    {activeMode !== 'ib' && ibProgram.partnershipConditionsMultipleSub && (
                      <div
                        style={{
                          display: 'flex',
                          gap: '20px',
                          alignItems: 'flex-start',
                        }}>
                        {/* 左侧：树结构 */}
                        <SubIBProgramsTree
                          treeData={ibProgram.partnershipConditionsMultipleSub.tree_data}
                          onSelectNode={handleSelectNode}
                          selectedNodeName={selectedNodeName}
                        />
                        {/* 右侧：奖励详情 */}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          {selectedRewardData ? (
                            <CurrentSubIBProgramsItem
                              currentIBProgram={{
                                name: selectedNodeName || ibProgram.partnerCode,
                                partnershipConditionsMultipleSub: selectedRewardData,
                              }}
                            />
                          ) : (
                            <div
                              style={{
                                padding: '40px',
                                textAlign: 'center',
                                color: '#999',
                                background: 'white',
                                borderRadius: '16px',
                                border: '1px solid rgba(44, 0, 143, 0.2)',
                              }}>
                              Please select a node from the tree to view reward details.
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
            </PanelTabs>
          ) : (
            <NoItemsPanel
              icon={noPartnersImage}
              captionSlug="noPartnershipProgramsCaption"
              descriptionSlug="noPartnershipProgramsDescription"
            />
          )}
        </PageContentPreloader>
      )}
    </PageLayout>
  );
};

PartnershipCurrentIBProgramsPage.propTypes = {
  partnershipSystem: PropTypes.string.isRequired,
  getCurrentIBPrograms: PropTypes.func.isRequired,
  currentIBPrograms: PropTypes.array.isRequired,
  currentIBProgramsIsLoaded: PropTypes.bool.isRequired,
  getSimpleIBSystemSettings: PropTypes.func.isRequired,
  simpleIBSystemSettings: PropTypes.object.isRequired,
  simpleIBSystemSettingsAreLoaded: PropTypes.bool.isRequired,
  location: PropTypes.object.isRequired,
};

export default withRouter(
  connect(
    state => ({
      partnershipSystem: state.interfaceConfig.partnership.partnershipSystem,
      currentIBPrograms: state.client.partnership.currentIBPrograms,
      currentIBProgramsIsLoaded: state.client.partnership.currentIBProgramsIsLoaded,
      simpleIBSystemSettings: state.client.partnership.simpleIBSystemSettings,
      simpleIBSystemSettingsAreLoaded: state.client.partnership.simpleIBSystemSettingsAreLoaded,
    }),
    {
      getCurrentIBPrograms: () => getCurrentIBPrograms.request(),
      getSimpleIBSystemSettings: () => getSimpleIBSystemSettings.request(),
    }
  )(PartnershipCurrentIBProgramsPage)
);
export { PartnershipCurrentIBProgramsPage };
